import React, {useContext, useEffect, useRef, useCallback} from 'react';
import Alpi from '../alpi/Alpi.jsx';
import Device from '@core/scripts/components/device';
import {Constants} from '../view-constants';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {DefaultFallback} from '@shopping-funnel/scripts/components/fallbacks/DefaultFallback.jsx';
import LoadMoreButton from '@article-overview/components/overview/LoadMoreButton.jsx';
import {useAlpiCutoffHeight} from '../hooks/use-alpi-cutoff-height.js';
import {useArticleNavigation} from '@article-overview/hooks/useArticleNavigation.ts';
import {
    LoadMoreButtonFadeoutPlaceholder
} from '@article-overview/components/overview/LoadMoreButtonFadeoutPlaceholder.jsx';
import {useScrollRestoration} from '@shopping-funnel/scripts/hooks/use-scroll-restoration.ts';
import {Survey} from '@article-overview/components/alpi/Survey.tsx';
import {buildPositionInList} from '@article-overview/utils/build-position-in-list';

function AlpiList({
    alpiList,
    shopContent,
    filterTrackingValue,
    pointOfSaleId,
    contentLoadedCounter,
    teaserContent,
    rankingTrackingValue,
    onLoadMore,
    isLoadingMore,
    hasMorePages,
    currentArticles,
    hits,
    filterTokens,
    searchQuery,
    firstItemIndex,
}) {
    const {loadMoreButtonTestGroup, texts, currentSearchParams, pageType} =
        useContext(articleOverviewStaticContext);

    const {updateScrollPosition} = useArticleNavigation();

    const currentMiddleElement = useRef(undefined);
    const alpiListRef = useRef(null);

    const firstAlpiHalfImageBottomOffset = useAlpiCutoffHeight(loadMoreButtonTestGroup, hasMorePages, alpiList);

    useEffect(() => {
        if (Device.isApp()) {
            window.addEventListener('scroll', keepScrollPosition, {passive: true});
        }
    }, []);

    const postProcess = useCallback(() => {
        currentSearchParams.current.delete(Constants.SEARCH_PARAMS_ANCHOR);
        currentSearchParams.current.delete(Constants.SEARCH_PARAMS_SCROLL_Y);
        currentSearchParams.current.delete(Constants.SEARCH_PARAMS_TEASER_ANCHOR);
    }, []);

    const teaserAnchorId = currentSearchParams.current.get(Constants.SEARCH_PARAMS_TEASER_ANCHOR);
    const anchorHook = currentSearchParams.current.get(Constants.SEARCH_PARAMS_ANCHOR);
    const slider = alpiListRef.current?.querySelector(`[jsh-alpi-slider-anchor-${anchorHook}]`);
    const alpi = slider?.closest('[jsh-alpi]') ?? null;

    const {jumpToPreviousLocation} = useScrollRestoration({
        condition: !!anchorHook && !!alpi,
        target: alpi
            ? {
                  type: 'element',
                  ref: {current: alpi},
                  waitForSelector: teaserContent?.content?.length > 0 ? '.article-overview__teaser-content' : undefined,
              }
            : null,
        deps: [alpiList, teaserContent, anchorHook],
        postProcess: postProcess,
    });

    useScrollRestoration({
        condition: !!teaserAnchorId,
        target: teaserAnchorId
            ? {
                  type: 'selector',
                  selector: `[data-teaser-id='${teaserAnchorId}']`,
                  waitForSelector: `[data-teaser-id='${teaserAnchorId}']`,
              }
            : null,
        deps: [alpiList, teaserContent, teaserAnchorId],
        postProcess: postProcess,
    });

    return (
        <>
            <div style={{position: 'relative'}}>
                <div
                    className="grid grid-cols-2 m:grid-cols-3 l:grid-cols-4 gap-0.5 w-full relative alpi-list__wrapper"
                    ref={alpiListRef}
                    style={{
                        overflow: 'hidden',
                        maxHeight: firstAlpiHalfImageBottomOffset ? `${firstAlpiHalfImageBottomOffset}px` : undefined,
                    }}
                >
                    {renderAlpis()}
                </div>
                {loadMoreButtonTestGroup && hasMorePages && firstAlpiHalfImageBottomOffset && (
                    <LoadMoreButtonFadeoutPlaceholder />
                )}
            </div>

            {loadMoreButtonTestGroup && hasMorePages && (
                <div className="flex justify-center">
                    <LoadMoreButton
                        loading={isLoadingMore}
                        hasMore={hasMorePages}
                        texts={texts}
                        onLoadMore={onLoadMore}
                        currentArticles={currentArticles}
                        hits={hits}
                    />
                </div>
            )}
        </>
    );

    function renderAlpis() {
        const alpis = [];
        // The two product positions the click event reports, both 1-based. Counted here rather than in
        // the tile, because only this loop knows how many products precede one. They count products
        // only, so a teaser sitting in the middle of the grid does not shift the position of every
        // product after it. `positionIndex` cannot be reused for that: it indexes the merged
        // article+teaser list, 0-based, and is what scroll restoration and Algolia's `apos` are keyed on.
        let productsSoFar = 0;

        const pushSurvey = (keySuffix) => {
            alpis.push(<Survey key={`survey-${keySuffix}`} searchQuery={searchQuery} />);
        };

        for (let i = 0; i < alpiList.length; i++) {
            const currentAlpi = alpiList[i];
            const positionOnPage =
                currentAlpi.gridItem?.displayType === Constants.GRIDITEM_ARTICLE ? ++productsSoFar : undefined;
            const positionInList = buildPositionInList(positionOnPage, firstItemIndex);
            const featuresKey = (currentAlpi.gridItem.features || []).join('-');
            // Use gridItem.id for stable key, fallback to index if not available. We need to include the features in the key to force a re-render when the features change
            let stableKey = currentAlpi.gridItem?.id ? `alpi-${currentAlpi.gridItem.id}-${i}` : `alpi-index-${i}`;
            stableKey = `${stableKey}-${featuresKey}`;
            if (pageType === 'srp' && i === 12) {
                pushSurvey(stableKey);
            }
            alpis.push(
                <DefaultFallback key={`fallback-${stableKey}`}>
                    <Alpi
                        key={stableKey}
                        alpiGiven={currentAlpi}
                        shopContent={shopContent}
                        positionIndex={i}
                        positionOnPage={positionOnPage}
                        positionInList={positionInList}
                        filterTrackingValue={filterTrackingValue}
                        rankingTrackingValue={rankingTrackingValue}
                        pointOfSaleId={pointOfSaleId}

                        teaserContent={teaserContent}
                        filterTokens={filterTokens}
                    />
                </DefaultFallback>,
            );
        }
        if (pageType === 'srp' && alpiList.length > 0 && alpiList.length <= 12) {
            pushSurvey(`alpi-list-end-${alpiList.length}`);
        }
        return alpis;
    }

    function keepScrollPosition() {
        // only as soon as jumpToPreviousLocation is false, otherwise we overwrite the actual entry position
        if (!jumpToPreviousLocation.current) {
            // cannot take the middle exactly, because with 2 or 4 alpis (S,L,XL) there is always a gap
            const middleElement = document.elementFromPoint(
                window.innerWidth / 2 - Constants.ALPI_SPACE_BETWEEN,
                window.innerHeight / 2,
            );
            const alpi = middleElement.closest('[jsh-alpi]');
            if (alpi) {
                const alpiHook = alpi.getAttribute('jsh-alpi');
                if (currentMiddleElement.current !== alpiHook) {
                    currentMiddleElement.current = alpiHook;
                    updateScrollPosition(alpiHook);
                }
            }
        }
    }
}

export default React.memo(AlpiList);
